/**
 * Top Movers — per-supplier best-sellers report inside Stock Master.
 *
 * Lets the buyer pick a supplier, a lookback window (days), and how many of
 * the top sellers to show (Top N). The system aggregates quantity sold from
 * the live `sales` collection and returns each product's PLU, name, qty
 * sold, revenue, current stock and reorder level so the user can spot which
 * top movers are currently under-stocked.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, TrendingUp, AlertTriangle, Download } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const N_PRESETS = [10, 25, 50, 100];
const DAY_PRESETS = [
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 6 months" },
  { value: "365", label: "Last 12 months" },
];

const fmtR = (n) =>
  `R ${Number(n || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const TopMoversModal = ({ open, onOpenChange, suppliers = [] }) => {
  const [supplierKey, setSupplierKey] = useState("");
  const [days, setDays] = useState("90");
  const [limit, setLimit] = useState(30);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state every time the modal opens so the buyer always starts clean.
  useEffect(() => {
    if (open) {
      setResult(null);
      setSupplierKey("");
      setDays("90");
      setLimit(30);
    }
  }, [open]);

  // Build the supplier picker options. Each item carries both id and name so
  // we can hit the legacy `supplier_name` path when a product was tagged via
  // the old free-text supplier field rather than supplier_id.
  const supplierOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of suppliers || []) {
      const name = s.name || s.supplier_name || "";
      if (!name || seen.has(name)) continue;
      seen.add(name);
      out.push({ key: s.id || `name:${name}`, id: s.id, name });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [suppliers]);

  const handleGenerate = async () => {
    if (!supplierKey) {
      toast.warning("Pick a supplier first.");
      return;
    }
    const supplier = supplierOptions.find((s) => s.key === supplierKey);
    if (!supplier) {
      toast.warning("Pick a supplier first.");
      return;
    }
    const n = Math.max(1, Math.min(500, parseInt(limit) || 30));
    setLoading(true);
    try {
      const params = { days: parseInt(days) || 90, limit: n };
      if (supplier.id) {
        params.supplier_id = supplier.id;
      } else {
        params.supplier_name = supplier.name;
      }
      const res = await axios.get(`${API}/api/products/top-movers`, { params });
      setResult(res.data);
      if (!res.data?.top_movers?.length) {
        toast.info(
          `No sales found in the last ${params.days} days for ${supplier.name}.`,
        );
      }
    } catch (err) {
      console.error("Top movers fetch failed:", err);
      toast.error(err.response?.data?.detail || "Failed to load top movers.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!result?.top_movers?.length) return;
    // Excel-friendly CSV. Guard against commas/quotes in names.
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "Rank",
      "PLU",
      "Name",
      "Qty Sold",
      "Revenue (R)",
      "Current Stock",
      "Reorder Level",
      "Below Reorder",
    ];
    const rows = result.top_movers.map((r) => [
      r.rank,
      r.plu_code || "",
      r.name || "",
      r.qty_sold,
      Number(r.revenue || 0).toFixed(2),
      r.current_stock,
      r.reorder_level,
      r.below_reorder ? "YES" : "",
    ]);
    const csv = [header, ...rows]
      .map((line) => line.map(escape).join(","))
      .join("\n");
    // Prepend UTF-8 BOM so Excel opens Rand-symbol cells correctly.
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const supplierSlug = (result.supplier_name || "supplier")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    const stamp = new Date().toISOString().slice(0, 10);
    const link = document.createElement("a");
    link.href = url;
    link.download = `top-movers-${supplierSlug}-${result.days}d-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} rows to CSV.`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="top-movers-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Top Movers — Best Sellers per Supplier
          </DialogTitle>
          <DialogDescription>
            Pick a supplier, a sales window and how many top sellers to list.
            Ranked by quantity sold. Below-reorder items are flagged so you can
            top them up first.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end pb-2">
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold text-gray-600">Supplier</Label>
            <Select value={supplierKey} onValueChange={setSupplierKey}>
              <SelectTrigger data-testid="top-movers-supplier" className="mt-1">
                <SelectValue placeholder="Choose a supplier…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {supplierOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600">Time window</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="top-movers-days" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAY_PRESETS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600">Top N</Label>
            <div className="flex gap-1 mt-1">
              <Input
                type="number"
                min={1}
                max={500}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="w-20"
                data-testid="top-movers-limit"
              />
              <Button
                onClick={handleGenerate}
                disabled={loading || !supplierKey}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="top-movers-generate"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pb-2">
          {N_PRESETS.map((n) => (
            <Button
              key={n}
              type="button"
              size="sm"
              variant={Number(limit) === n ? "default" : "outline"}
              onClick={() => setLimit(n)}
              data-testid={`top-movers-preset-${n}`}
            >
              Top {n}
            </Button>
          ))}
        </div>

        <div className="overflow-auto flex-1 border rounded-md">
          {result && result.top_movers?.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 text-left">
                <tr>
                  <th className="px-3 py-2 w-12">#</th>
                  <th className="px-3 py-2">PLU</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2 text-right">Qty Sold</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                  <th className="px-3 py-2 text-right">Current Stock</th>
                  <th className="px-3 py-2 text-right">Reorder Level</th>
                </tr>
              </thead>
              <tbody>
                {result.top_movers.map((row) => (
                  <tr
                    key={row.id}
                    className={
                      row.below_reorder
                        ? "border-t bg-amber-50/60"
                        : "border-t hover:bg-gray-50"
                    }
                    data-testid={`top-movers-row-${row.id}`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-500">{row.rank}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.plu_code || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{row.name}</span>
                        {row.below_reorder && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-0.5 bg-amber-200 text-amber-900"
                            title="Current stock is at or below reorder level"
                          >
                            <AlertTriangle className="h-3 w-3" /> BELOW REORDER
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.qty_sold}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtR(row.revenue)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.current_stock}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.reorder_level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : result ? (
            <div className="text-sm text-gray-500 py-12 text-center">
              No sales were recorded for {result.supplier_name || "this supplier"} in
              the last {result.days} days.
            </div>
          ) : (
            <div className="text-sm text-gray-400 py-12 text-center">
              Choose a supplier and click <span className="font-semibold">Generate</span>.
            </div>
          )}
        </div>

        {result && result.top_movers?.length > 0 && (
          <div className="pt-2 flex items-center justify-between gap-4 flex-wrap">
            <div className="text-xs text-gray-500 flex flex-wrap gap-x-4">
              <span>
                Showing top <strong>{result.top_movers.length}</strong> of{" "}
                <strong>{result.matched_products}</strong> products that had sales.
              </span>
              <span>Scanned <strong>{result.scanned_products}</strong> products under {result.supplier_name}.</span>
              <span>Window: last <strong>{result.days}</strong> days.</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
              data-testid="top-movers-export-csv"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
